// ============================================================================
// JARVISTRAVEL - ERROR BOUNDARY
// Catches render crashes and shows an on-brand recovery UI. Used both at the
// app root (full-screen default) and per-route (resetKeys clear the error when
// the user navigates, plus a function-form fallback for a localized recover card).
// The full-screen page holds instead once the same error comes straight back.
// ============================================================================

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { captureException } from '@/app/utils/observability';
import { isReloadingForStaleFile } from '@/app/utils/appUpdate';

interface Props {
  children: ReactNode;
  /** A replacement for the default full-screen page. Either a static node, or a
   *  render function that receives a `reset` callback (to re-attempt the subtree). */
  fallback?: ReactNode | ((reset: () => void) => ReactNode);
  /** When any value here changes while errored, the boundary clears itself — wire
   *  to `location.pathname` so a route crash recovers on navigation. The full-screen
   *  page holds instead once the error it cleared comes straight back (JAR-2755). */
  resetKeys?: unknown[];
}

interface State {
  hasError: boolean;
  error: Error | null;
  /** The error the last clear moved on from came straight back: the full-screen page holds and says so. */
  again: boolean;
}

/** A crash screen is for a person to read, not a place to dump a response body. */
const CRASH_MESSAGE_MAX = 300;

function clampForDisplay(message: string): string {
  const trimmed = (message ?? '').trim();
  if (!trimmed) return 'No further detail.';
  return trimmed.length > CRASH_MESSAGE_MAX ? `${trimmed.slice(0, CRASH_MESSAGE_MAX)}…` : trimmed;
}

/** What makes two caught errors the same one: thrown afresh on each render, it's a new object each time. */
const signature = (error: unknown): string =>
  error instanceof Error ? `${error.name}: ${error.message}` : String(error);

export class ErrorBoundary extends Component<Props, State> {
  /** The error the last clear moved on from, and the keys it cleared at. */
  private cleared: { signature: string; keys?: unknown[] } | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, again: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // The same error caught with no navigation since the clear will keep coming
    // back, and clearing it on the next one would only show this screen again
    // (JAR-2755). A boundary with its own fallback sits inside the app, whose nav
    // still works around it, so it always clears.
    if (this.props.fallback === undefined && this.cleared?.signature === signature(error)
      && !this.resetKeysChanged(this.cleared.keys, this.props.resetKeys)) {
      this.setState({ again: true });
    }
    // A page reloading into a new build is already fixing this, so it isn't one
    // to report, and it shows nothing in the moment before the page goes.
    if (isReloadingForStaleFile()) return;
    captureException(error, { source: 'ErrorBoundary', componentStack: errorInfo.componentStack });
  }

  componentDidUpdate(prevProps: Props, prevState: State): void {
    // A navigation clears an error that was showing before it, unless the screen
    // is held. One the navigation brought is left: the screen would only throw it
    // again, and the same crash would be caught and reported twice.
    if (prevState.hasError && this.state.hasError && !this.state.again
      && this.resetKeysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.handleReset();
    }
  }

  private resetKeysChanged(prev?: unknown[], next?: unknown[]): boolean {
    if (!prev || !next) return false;
    if (prev.length !== next.length) return true;
    return prev.some((value, i) => !Object.is(value, next[i]));
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleReset = (): void => {
    this.cleared = { signature: signature(this.state.error), keys: this.props.resetKeys };
    this.setState({ hasError: false, error: null, again: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (isReloadingForStaleFile()) return null;
      const { fallback } = this.props;
      if (typeof fallback === 'function') return fallback(this.handleReset);
      if (fallback !== undefined) return fallback;
      const { again } = this.state;
      return (
        <div className="min-h-[100dvh] flex items-center justify-center bg-bg px-4">
          <div className="max-w-md w-full text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-danger/10 text-danger rounded-2xl flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8" strokeWidth={1.75} aria-hidden="true" />
              </div>
              <h1 className="text-2xl font-bold text-ink mb-2">Something went wrong</h1>
              <p className="text-body mb-2">
                {again
                  ? 'The same error came straight back. Reload the page, or head back home.'
                  : 'An unexpected error occurred. Please try reloading the page.'}
              </p>
              {this.state.error && (
                <details className="text-left mt-4">
                  <summary className="text-sm text-muted cursor-pointer hover:text-body">Error details</summary>
                  {/*
                    Clamped. This is the one surface that renders a message from
                    ANY error — including an ApiRequestError whose message could
                    be a proxy's HTML error page — and it does so in a <pre> with
                    no length limit (review M3). Text-rendered, so not XSS, but a
                    crash screen showing a wall of someone else's markup is not a
                    crash screen anyone can act on.
                  */}
                  <pre className="mt-2 text-xs text-danger bg-danger/5 p-3 rounded-lg overflow-auto max-h-32 whitespace-pre-wrap break-words">
                    {clampForDisplay(this.state.error.message)}
                  </pre>
                </details>
              )}
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="px-6 py-2.5 bg-brand text-brand-fg rounded-lg hover:bg-brand/90 transition-colors font-semibold"
              >
                Reload page
              </button>
              {/* Held, a way home replaces Try again. A full load, not a route change, so it
                  also drops whatever the app was holding that keeps throwing. */}
              {again ? (
                <a
                  href="/trips"
                  className="px-6 py-2.5 bg-surface-raised text-body rounded-lg hover:text-ink transition-colors font-semibold"
                >
                  Back to home
                </a>
              ) : (
                <button
                  onClick={this.handleReset}
                  className="px-6 py-2.5 bg-surface-raised text-body rounded-lg hover:text-ink transition-colors font-semibold"
                >
                  Try again
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
